import { useEffect, useState } from "react";
import styles from "./new-insight.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { IParagraph, IPostInsight } from "../../../interfaces/insightPost";
import ModalAddItem from "./modal-add-item";
import PostPreview from "./post-preview";
import { IUser } from "../../../interfaces/user";

type Props = {
  user: IUser;
  insight?: IPostInsight;
  onClickSairInsight?: () => void;
  onSaveInsight?: (newInsight: IPostInsight) => Promise<void>;
  onUploadInsight?: (existingInsight: IPostInsight) => Promise<void>;
  setIsEdit: (isEdit: boolean) => void;
};

const NewInsight = ({
  user,
  insight,
  onClickSairInsight,
  onSaveInsight,
  onUploadInsight,
  setIsEdit,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<IPostInsight>({});
  const [paragraphs, setParagraphs] = useState<Array<IParagraph>>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const onAddParagraph = (newInput: IParagraph) => {
    const paragraphsArr = [...paragraphs, newInput];
    setParagraphs(paragraphsArr);

    setData({ ...data, paragraphs: paragraphsArr });
  };

  const LoadingOverlay = () => (
    <div className={styles.loadingOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );

  useEffect(() => {
    const onLoad = async (token: string) => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          console.error("Erro: API URL não definida.");
          return;
        }

        const response = await fetch(`${apiUrl}/posts?id=${insight?._id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const resData = await response.json();

        if (!response.ok) {
          alert(
            `${resData.message} ${resData.details}` || "Erro ao buscar insight."
          );
          return;
        }

        const post = resData.data;
        post.username = `${post.user?.firstname} ${post.user?.lastname}`;

        return post;
      } catch (error) {
        alert("Erro ao buscar insight: " + error);
        return;
      }
    };

    if (insight?._id) {
      const getCookie = (name: string) => {
        const match = document.cookie.match(`(^|;)\\s*${name}=([^;]+)`);
        return match ? match[2] : null;
      };

      const token = getCookie("token");
      if (token) {
        setLoading(true);

        onLoad(token)
          .then((post) => {
            if (post) {
              post.date = post.date.split("T")[0];
              setData(post);
              setParagraphs(post.paragraphs || []);
            }
          })
          .catch((error) => {
            console.error("Erro ao buscar insight: " + error);
            setData({});
            setParagraphs([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      const today = new Date().toISOString().split("T")[0];
      setData({ username: `${user.firstname} ${user.lastname}`, date: today });
      setParagraphs([]);
    }
  }, [insight, user.firstname, user.lastname]);

  return (
    <>
      {loading && <LoadingOverlay />}
      <div className={styles.page}>
        <div className={styles.container_title_new_div}>
          <h1 className={styles.container_title}>Novo Insight</h1>
        </div>
        <div className={`${styles.grid_list}`}>
          <div className={styles.form}>
            <div className={`${styles.form_input_div} ${styles.w85}`}>
              <label htmlFor="title" className={styles.form_label}>
                Título:
              </label>
              <input
                type="text"
                id="title"
                className={styles.form_input}
                value={data?.title || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setData({ ...data, title: e.target.value })
                }
              />
            </div>
            <div className={`${styles.form_input_div} ${styles.w15}`}>
              <label htmlFor="date" className={styles.form_label}>
                Data:
              </label>
              <input
                type="date"
                id="date"
                className={styles.form_input}
                value={data?.date || ""}
                onChange={(e) =>
                  setData({
                    ...data,
                    date: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className={styles.form}>
            <div className={`${styles.form_input_div} ${styles.w100}`}>
              <label htmlFor="descricao" className={styles.form_label}>
                Descrição:
              </label>
              <textarea
                className={styles.textarea}
                rows={8}
                id="descricao"
                value={data?.description || ""}
                onChange={(e) =>
                  setData({ ...data, description: e.target.value })
                }
              />
            </div>
          </div>
          <div className={styles.form}>
            <div
              className={`${styles.form_img_input_div} ${styles.mt1} ${styles.w100}`}
            >
              <div>
                <span className={styles.form_label}>Capa:</span>
                {data?.cover ? (
                  <div className={styles.image_preview}>
                    <img
                      src={data.cover}
                      alt="Capa"
                      className={styles.preview_img}
                    />
                    <button
                      className={styles.upload_img_btn}
                      onClick={() => {
                        setData({ ...data, cover: "" });
                        (
                          document.getElementById("cover") as HTMLInputElement
                        ).value = "";
                      }}
                    >
                      <span>Remover Capa</span>
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="cover" className={styles.upload_img_btn}>
                    <span>Upload</span>
                    <FontAwesomeIcon icon={faUpload} size="sm" />
                  </label>
                )}
              </div>
              <div className={styles.image_row}>
                <div className={styles.form_image_upload}>
                  <input
                    type="file"
                    id="cover"
                    className={styles.form_input_img}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files && e.target.files[0]) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setData({
                            ...data,
                            coverFile: e.target.files
                              ? e.target.files[0]
                              : null,
                            cover: event.target?.result as string,
                          });
                        };
                        reader.readAsDataURL(e.target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.new_items} ${styles.mt1}`}>
            <div
              className={styles.add_btn_div}
              onClick={() => setIsModalOpen(true)}
            >
              <span className={styles.add_text}>
                Adicionar novo texto ou image/vídeo
              </span>
              <button className={styles.add_btn}>
                <FontAwesomeIcon icon={faAdd} />
              </button>
            </div>
          </div>
        </div>
        <div className={`${styles.form_group_end}`}>
          <button
            type="submit"
            className={styles.action_cancel_btn_end}
            onClick={() => {
              setData({});
              setParagraphs([]);
              if (onClickSairInsight) onClickSairInsight();
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={styles.action_btn_end}
            disabled={loading}
            onClick={() => {
              if (insight?._id) {
                if (onUploadInsight) {
                  setLoading(true);
                  onUploadInsight(data)
                    .then(() => {
                      setIsEdit(false);
                      setLoading(false);
                    })
                    .catch(() => setLoading(false));
                }
              } else {
                if (onSaveInsight) {
                  setLoading(true);
                  onSaveInsight(data)
                    .then(() => {
                      setIsEdit(false);
                      setLoading(false);
                    })
                    .catch(() => setLoading(false));
                }
              }
            }}
          >
            Salvar
          </button>
        </div>
      </div>
      <div className={styles.post_preview}>
        <h2 className={styles.preview_title}>Preview</h2>
        <PostPreview
          post={data}
          paragraphs={paragraphs}
          setParagraphs={setParagraphs}
        />
      </div>
      <ModalAddItem
        visible={isModalOpen}
        onCreate={(newParagraph: IParagraph) => {
          onAddParagraph(newParagraph);
          setIsModalOpen(false);
        }}
        onCancel={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default NewInsight;
